import { useCallback } from 'react';

import { SafeHtml } from '@bcp-channels-sharedcomponents-reactjs/core';
import { useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import {
	BaseModal,
	Box,
	Button,
	CSSProperties,
	Image,
	Overlay,
	Typography,
} from '@bcp-nextgen-dx-component-factory/design-system';
import { NavClose } from '@bcp-nextgen-dx-component-factory/icons-accolade';
import { useGetCurrentBreakpoint, useHideScroll, useIsVisibleModal, useModal } from '@hooks';
import { useServicesStore, useTempStore } from '@store';
import { ExclamationIcon } from '@themes';

export const AdjustedPaymentTermSimulationModalBCP = () => {
	const { currentView } = useGetCurrentBreakpoint();
	const translate = useTranslation();
	const { hide: hideAdjustedPaymentTermSimulation } = useModal('adjustedPaymentTermSimulationModal');
	const simulationOverview = useServicesStore((state) => state.simulationOverview);
	const setShowedAdjustedPaymentTermSimulation = useTempStore(
		(state) => state.setShowedAdjustedPaymentTermSimulation
	);
	const { enable } = useHideScroll();
	const handleOnClickClose = useCallback(() => {
		enable();
		setShowedAdjustedPaymentTermSimulation();
		hideAdjustedPaymentTermSimulation();
	}, [hideAdjustedPaymentTermSimulation]);
	const isVisible = useIsVisibleModal('adjustedPaymentTermSimulationModal');

	const generalStyles = {
		modalConfig: {
			background: '#FFFFFF',
			borderRadius: '24px',
		},
		modalContent: {
			flexDirection: 'column',
		},
		bigIconBox: {
			marginBottom: '24px',
		},
		closeButtonBox: {
			alignSelf: 'end',
		},
		messageBox: {
			textAlign: 'center',
			flexDirection: 'column',
			marginBottom: '32px',
		},
		modalTextContent: {
			flexDirection: 'column',
			alignItems: 'center',
		},
		buttonContainer: { justifyContent: 'center' },
	};

	const styles = {
		...generalStyles,
		smallView: {
			...generalStyles,
			modalConfig: {
				...generalStyles.modalConfig,
			},
			messageBox: {
				...generalStyles.messageBox,
				rowGap: '8px',
			},
			modalTextContent: {
				...generalStyles.modalTextContent,
				width: '348px',
				padding: '48px 24px 32px',
			},
			buttonContainer: {
				...generalStyles.buttonContainer,
				width: '100%',
			},
		},
		tabletView: {
			...generalStyles,
			modalConfig: {
				...generalStyles.modalConfig,
			},
			closeButtonBox: {
				...generalStyles.closeButtonBox,
				padding: '24px',
			},
			messageBox: {
				...generalStyles.messageBox,
				rowGap: '16px',
			},
			modalTextContent: {
				...generalStyles.modalTextContent,
				width: '536px',
				padding: '0 24px 72px',
			},
			buttonContainer: {
				...generalStyles.buttonContainer,
			},
		},
		bigView: {
			...generalStyles,
			modalConfig: {
				...generalStyles.modalConfig,
			},
			modalContent: {
				...generalStyles.modalContent,
				padding: '24px 24px 72px 24px',
			},
			closeButtonBox: {
				...generalStyles.closeButtonBox,
				marginBottom: '24px',
			},
			messageBox: {
				...generalStyles.messageBox,
				rowGap: '16px',
			},
			modalTextContent: {
				...generalStyles.modalTextContent,
				width: '700px',
			},
			buttonContainer: {
				...generalStyles.buttonContainer,
			},
		},
	};

	return (
		<BaseModal visible={isVisible} externalBackdrop={<Overlay onPress={handleOnClickClose} />}>
			<Box styles={styles[currentView].modalConfig}>
				<Box styles={styles[currentView].modalContent as CSSProperties}>
					{currentView !== 'smallView' && (
						<Box styles={styles[currentView].closeButtonBox} onPress={handleOnClickClose}>
							<Image
								source={NavClose.Outline}
								svgProps={{
									fill: '#6A717C',
									width: '24px',
									height: '24px',
								}}
							/>
						</Box>
					)}
					<Box styles={styles[currentView].modalTextContent as CSSProperties}>
						<Box styles={styles[currentView].bigIconBox}>
							<ExclamationIcon />
						</Box>
						<Box styles={styles[currentView].messageBox as CSSProperties}>
							<Typography appearance="subtitle-100-04">
								{translate('simulator.modals.adjustedPaymentTermSimulation.termAdjusted')}
							</Typography>
							<Typography appearance="body-100-02">
								{simulationOverview && (
									<SafeHtml html={simulationOverview?.adjustedTermMessages?.modal || ''} />
								)}
							</Typography>
						</Box>
						<Box styles={styles[currentView].buttonContainer}>
							<Button
								size={
									currentView === 'smallView'
										? 'xLarge'
										: currentView === 'tabletView'
										? 'large'
										: 'medium'
								}
								fixedWidth={currentView === 'smallView'}
								onPress={handleOnClickClose}
							>
								{translate('simulator.modals.adjustedPaymentTermSimulation.seeCalculation')}
							</Button>
						</Box>
					</Box>
				</Box>
			</Box>
		</BaseModal>
	);
};
